import { useCallback, useMemo, type ReactNode } from 'react';
import { PartyChatContext, type PartyChatTab } from '@sorrem-client/ui/features/Hud';
import { isShallowEqual } from '@sorrem-client/ui/hooks';
import { sendPartyChatMessage, usePartyRealtimeSelector } from '../party-realtime-store';
import { ChatPanel } from './ChatPanel';

interface PartyChatProviderProps {
    /** The page's crucible; reading the party store connects to it, as the page itself does. */
    readonly crucibleId: string | null;
    /** Whether anybody else can be in the crucible: a crucible only one can enter has no chat. */
    readonly isParty: boolean;
    readonly children: ReactNode;
}

/**
 * Provides the party's Chat tab (`PartyChatContext`) at the gathering and on
 * through the run, as the party channel stays open while it plays. It reads
 * the chat from the party store itself, so a new message re-renders this
 * and the party tabs, never the crucible page or the run's HUD around them.
 */
export function PartyChatProvider({ crucibleId, isParty, children }: PartyChatProviderProps): JSX.Element {
    const { chatMessages, isConnected } = usePartyRealtimeSelector(
        crucibleId,
        (storeState) => ({ chatMessages: storeState.chatMessages, isConnected: storeState.isConnected }),
        isShallowEqual,
    );
    const sendChatMessage = useCallback((body: string): void => {
        if (crucibleId !== null) {
            sendPartyChatMessage(crucibleId, body);
        }
    }, [crucibleId]);
    const partyChat = useMemo((): PartyChatTab | null => (isParty
        ? {
            content: <ChatPanel isConnected={isConnected} chatMessages={chatMessages} onSendMessage={sendChatMessage} />,
            latestMessageId: chatMessages.at(-1)?.id ?? null,
        }
        : null), [chatMessages, isConnected, isParty, sendChatMessage]);

    return <PartyChatContext.Provider value={partyChat}>{children}</PartyChatContext.Provider>;
}
